import { useEffect } from 'react';

/**
 * Puts the clinic's name in the browser tab and its mark on the favicon.
 *
 * Which mark is the caller's decision, not this hook's: `BrandingContext` passes the clinic's
 * own favicon when it has uploaded one and its logo when it has not. This end only knows there
 * is a URL, which is why the fallback is not written here -- there is exactly one tab and
 * exactly one rule about what goes in it.
 *
 * <h2>Why this can exist now</h2>
 *
 * These three -- the title, the favicon and the PWA manifest -- were the documented exceptions
 * to per-clinic branding, on the grounds that one static build cannot vary them. That was true
 * while the only thing naming a clinic was a token: `index.html` is served before any
 * JavaScript runs, so by the time the app knew whose it was, the tab had already said Carefull.
 *
 * The hostname changes it. It is known before the first request, so the app can correct both as
 * soon as the public tenant context resolves -- which, thanks to the host-keyed cache, is
 * usually the first frame.
 *
 * <h2>The manifest is not this hook's</h2>
 *
 * The browser fetches it on its own terms and nothing here can re-dress it afterwards, so
 * `index.html` names an API route that answers per hostname instead of a file -- the same
 * answer the static icon tags got, for the readers that never wait for this hook.
 *
 * <h2>Restoring on the way out</h2>
 *
 * Both are reverted when the clinic goes away, which is what makes this safe to run on the
 * platform's own address: `carefull.ro` never sets either, and a test that renders a clinic
 * does not leave the tab renamed for the next one.
 */
export const useDocumentIdentity = (
    clinicName: string | null | undefined,
    iconUrl: string | null | undefined
): void => {
    useEffect(() => {
        if (!clinicName) {
            return;
        }

        const previous: string = document.title;
        document.title = clinicName;

        return () => {
            document.title = previous;
        };
    }, [clinicName]);

    useEffect(() => {
        if (!iconUrl) {
            return;
        }

        // The existing tag rather than a new one: browsers take the last icon link they see,
        // so appending would work by accident and leave a duplicate behind on every change.
        const link: HTMLLinkElement | null = document.querySelector("link[rel='icon']");

        if (!link) {
            return;
        }

        const previous: string = link.href;
        link.href = iconUrl;

        return () => {
            link.href = previous;
        };
    }, [iconUrl]);
};
